import React, { useContext, useEffect, useMemo } from "react";
import classnames from "classnames";
import { ProductsContext } from "contexts/attributes";
import AttributeInputField from "components/attributes/compare-table/attribute-input-field";
import { findField } from "components/attributes/compare-table/field-builder";
import { attributeUUID } from "shared/attribute-uuid";
import Notice from "./notice";
import { AttributeFeedback } from "components/attributes/attribute-feedback";
import { isAttributeFeedbackSupported } from "components/attributes/attribute-feedback/lib/helpers";
import { useAttributes } from "../../../shared/hooks/use-attributes";

export const ProfilingAttributeInput = ({ attribute, taskType, codingWipKey, isFinalized, isCompleted, taskKey, fetchDefinitions }) => {
    const { refresh, products, updateAttributes } = useContext(ProductsContext);
    const { data: attributesData, forceRefreshAttributes } = useAttributes();
    const showFeedback = isAttributeFeedbackSupported(attribute, taskType) && attributesData?.hasOwnProperty(attribute.uuid);

    const hasNoticeIncluded = useMemo(
        () => attributeUUID.cxHTaxonomy === attribute.uuid || attributeUUID.inhouseCategories === attribute.uuid,
        [attribute.uuid]
    );
    const noticeContainer = classnames({
        "m-t-6": true,
        "m-b-6": true,
        "notice-container": hasNoticeIncluded,
    });
    const notice = classnames({
        notice: hasNoticeIncluded,
    });
    const Field = useMemo(() => findField(attribute.componentType), [attribute.componentType]);
    const isReadOnly = !!attribute.readonlyOnTaskType && !attribute.required;

    useEffect(() => {
        if (products[codingWipKey] && products[codingWipKey].refresh) {
            fetchDefinitions();
            forceRefreshAttributes();
            products[codingWipKey].refresh = "";
        }
    }, [products]);

    const updateOnChange = async (attr, value) => {
        if (products[codingWipKey][attr.uuid] === value) {
            return;
        }

        const action = await updateAttributes(codingWipKey, { [attr.uuid]: value });

        // TODO: ProductsContext and AttributesContext are not in sync, refactor this to avoid calling 'getWips' twice
        if (action && action.refresh) {
            refresh(); // refresh product
            fetchDefinitions();
            forceRefreshAttributes();
        }
    };

    return (
        <div
            key={`productData_${codingWipKey}`}
            className={noticeContainer}
        >
            <div className={notice}>
                {hasNoticeIncluded && <Notice title="These fields must be completed in order to enter Identifying Attributes." />}
                <label>{attribute.name}: </label>
            </div>
            <div
                className={showFeedback ? "attribute-feedback-cell" : undefined}
                data-testid="attribute-input-row"
            >
                <div className={showFeedback ? "attribute-feedback-field" : undefined}>
                    <AttributeInputField
                        Field={Field}
                        attribute={attribute}
                        useAttributes={{
                            data: products[codingWipKey],
                            update: updateOnChange,
                            add: updateOnChange,
                        }}
                        disabled={isFinalized || isReadOnly}
                        prefix={codingWipKey}
                    />
                </div>
                {showFeedback && (
                    <AttributeFeedback
                        attr={attribute}
                        taskType={taskType}
                        taskKey={taskKey}
                        canEdit={!isCompleted}
                    />
                )}
            </div>
        </div>
    );
};
